/**
 * chartAxis.ts — the money AXIS formatter (§24 C-2), distinct from the card
 * formatters (fmtMoney / fmtCompact), which compact one value at a time.
 *
 * What was wrong with a card formatter on an axis (§24.2.1): recharts lays
 * nice ticks from its engine (Fourth Coffee: 0 · 55M · 110M · 165M · 220M
 * pence), and `fmtMoney` then compacted each tick on its own — `£550k` next
 * to `£1.1M` (mixed units on one axis) and `(1.65).toFixed(1) === "1.6"`, a
 * gridline labelled £50,000 under its own value on every tenant whose top
 * tick lands on an odd half-million.
 *
 * The rule here: ONE unit per axis, chosen from the TOP tick (M at or above
 * £1M, k at or above £1,000, else pounds), and every tick printed EXACTLY —
 * trailing zeros trimmed, never rounded to a fixed decimal count. The ticks
 * come from recharts' own engine (recharts-scale getNiceTickValues, the same
 * call YAxis makes for `domain [0,'auto']`), so the unit and the drawn ticks
 * are one computation: the chart passes `ticks` + `domain` from here and the
 * axis cannot disagree with its labels.
 *
 * Units: the engine and the ticks work in the DATA's unit. The revenue charts
 * carry integer PENCE on the wire; the Builder's chartRows are POUNDS. Callers
 * say which, and `format` takes the data's unit back.
 */
import { getNiceTickValues } from 'recharts-scale'
import { currencySymbol } from '../../../../lib/money'

/** recharts' YAxis default — five ticks, four intervals. */
export const AXIS_TICK_COUNT = 5

export type AxisUnit = 'M' | 'k' | ''

export interface MoneyAxis {
  /** The nice ticks, in the data's unit, ascending from 0. */
  ticks: number[]
  /** [0, top tick] — pass as YAxis `domain` so the scale ends ON the top tick. */
  domain: [number, number]
  /** The one unit every tick on this axis is printed in. */
  unit: AxisUnit
  /** Tick → label, in the data's unit — EXACT (ticks are nice numbers). */
  format: (value: number) => string
  /** A bar VALUE → its compact label in the same unit (§24 C-4): two
   *  decimals at most, trailing zeros trimmed — £2.02M, £8k, £1.23k. A value
   *  is any figure, so this one rounds; the exact figure is the tooltip's
   *  (C-9), never the label's. */
  formatCompact: (value: number) => string
  /** True when nothing on the axis is above zero (the engine then emits 0..4
   *  in the raw unit — the caller renders an empty state instead, §24 C-5). */
  allZero: boolean
}

/** recharts' own nice ticks for a [0, max] money axis. */
export function niceTicks(max: number, tickCount = AXIS_TICK_COUNT): number[] {
  return getNiceTickValues([0, Math.max(0, max)], tickCount, true)
}

/** One unit for the whole axis, from the top tick in POUNDS. */
export function axisUnit(topTickPounds: number): AxisUnit {
  if (topTickPounds >= 1_000_000) return 'M'
  if (topTickPounds >= 1_000) return 'k'
  return ''
}

const UNIT_DIVISOR: Record<AxisUnit, number> = { M: 1_000_000, k: 1_000, '': 1 }

// Exact, not rounded: six places covers every nice tick (the engine's step
// mantissa sits on a 0.05 grid and the unit shifts it by at most one decade,
// so no nice tick needs more than three), and the trim removes only zeros.
function exactDecimal(n: number): string {
  const s = n.toFixed(6)
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}

/** A tick in POUNDS → its label in the axis' unit: 1_650_000 'M' → "£1.65M",
 *  350 'k' → "£0.35k", 800 '' → "£800". Symbol rule as fmtMoney/money():
 *  £/$/€ unspaced, any other code prefixed with a space. */
export function fmtAxisMoney(pounds: number, unit: AxisUnit, currency = 'GBP'): string {
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  // The origin is bare ("£0", never "£0M"): zero has no unit to be in.
  if (pounds === 0) return `${sym}0`
  return `${sym}${exactDecimal(pounds / UNIT_DIVISOR[unit])}${unit}`
}

/** A VALUE in POUNDS → compact in the axis' unit: 2_022_276.46 'M' → "£2.02M",
 *  1_234 'k' → "£1.23k", 8_000 'k' → "£8k". Two decimals, then the same trim. */
export function fmtAxisMoneyCompact(pounds: number, unit: AxisUnit, currency = 'GBP'): string {
  const n = pounds / UNIT_DIVISOR[unit]
  return fmtAxisMoney(Math.round(n * 100) / 100 * UNIT_DIVISOR[unit], unit, currency)
}

/**
 * The axis for a set of values. `pence: true` for wire money (the revenue
 * charts), `pence: false` for pounds (the Builder's chartRows).
 */
export function moneyAxis(values: number[], opts: { pence: boolean; currency?: string }): MoneyAxis {
  const max = values.reduce((m, v) => (v > m ? v : m), 0)
  const ticks = niceTicks(max)
  const top = ticks[ticks.length - 1]
  const scale = opts.pence ? 100 : 1
  const unit = axisUnit(top / scale)
  const currency = opts.currency ?? 'GBP'
  return {
    ticks,
    domain: [0, top],
    unit,
    format: (v: number) => fmtAxisMoney(v / scale, unit, currency),
    formatCompact: (v: number) => fmtAxisMoneyCompact(v / scale, unit, currency),
    allZero: max <= 0,
  }
}
